import type { GeoLocation, GeoLocationsList } from '@openx/types/geoLocation';
import type { GeoSearchOutput } from '@openx/types/gqlTypes';
import type { GeoDataApi } from '@openx/types/targeting/geographic';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { GeoSearchDocument } from '../../../../graphql/query/geo/search.generated';
import { getGraphQLRequestFromClient } from '../../../../modules/graphql/types';

export const useFetchByGeoData = () => {
  const queryClient = useQueryClient();

  const runSearch = useCallback(
    async (phrase: string, limit?: number): Promise<GeoLocationsList> => {
      // graphQLRequest already reports the error; resolve to an empty result like the former Apollo lazy query did
      const res = await queryClient
        .fetchQuery({
          queryKey: [QueryKeys.SEARCH, 'geo', phrase, limit],
          queryFn: ({ client }) => getGraphQLRequestFromClient(client)(GeoSearchDocument, { phrase, limit }),
        })
        .catch(() => null);

      const geoRes: GeoSearchOutput[] = res?.geoSearch ?? ([] as GeoSearchOutput[]);
      const geoOut: GeoLocationsList = [];

      // Remap the gql response to the GeoLocation type
      // TODO: UIG-540 Check null values for api update
      geoRes.forEach(item => {
        const obj: GeoLocation = {
          city: item.city,
          code: item.code,
          continent: item.continent,
          country: item.country,
          dma: item.dma,
          id: item.id,
          latitude: `${item.latitude}`,
          longitude: `${item.longitude}`,
          msa: item.msa,
          name: item.name,
          postal_code: item.postal_code,
          region: item.region,
          state: item.state,
          type: item.type,
          'type-id': item.type_id,
        };
        geoOut.push(obj);
      });

      return geoOut;
    },
    [queryClient]
  );

  const fetchByGeoData = useCallback(
    (geoData: GeoDataApi): Promise<GeoLocationsList> => {
      const qArr: string[] = [];
      let limit = 0;
      Object.entries(geoData).forEach(([key, value]) => {
        const values = value?.split(',').map(q => `"${q}"`) ?? [];
        limit += values.length;
        qArr.push(`(type:${key} AND id:(${values.join(' OR ')}))`);
      });

      const qPhrase = qArr.join(' OR ');
      const phrase = `(${qPhrase})`;

      return runSearch(phrase, limit);
    },
    [runSearch]
  );

  return { fetchByGeoData };
};
